/*
 * pv-series-grid.js
 * ---------------------------------------------------------------------------
 * Serien-Gruppierung im Collection-Grid (main-collection-product-grid).
 *
 * Problem: Shopify paginiert nach einer festen Anzahl Produkte. Die Gruppierung
 * nach Serie passiert aber nur innerhalb einer Seite - dadurch konnte eine Serie
 * am Seitenumbruch zerrissen werden (z. B. 1 Werk der Serie allein auf Seite 2).
 *
 * Loesung: Seite 1 wird serverseitig gerendert, alle weiteren Seiten werden per
 * Section Rendering API nachgeladen und in die bereits vorhandenen Serien-Bloecke
 * einsortiert. Ergebnis: alle Werke auf einer Seite, jede Serie komplett.
 * ---------------------------------------------------------------------------
 */
(function () {
  'use strict';

  var GROUP_SELECTOR = '.pv-series-group';
  var REF_ATTR = 'data-pv-series-ref';
  var POSITION_ATTR = 'data-pv-series-position';

  function toArray(nodeList) {
    return Array.prototype.slice.call(nodeList || []);
  }

  /* Doppelte Component-Styles/Scripts aus nachgeladenen Fragmenten entfernen -
     die sind durch Seite 1 bereits im Dokument. */
  function stripDuplicateAssets(node) {
    toArray(node.querySelectorAll('style, link[rel="stylesheet"], script')).forEach(function (el) {
      el.parentNode.removeChild(el);
    });
    return node;
  }

  function findGroup(container, ref) {
    var groups = toArray(container.querySelectorAll(GROUP_SELECTOR));
    for (var i = 0; i < groups.length; i++) {
      if (groups[i].getAttribute(REF_ATTR) === ref) return groups[i];
    }
    return null;
  }

  /* Position aus der vom Kuenstler gewaehlten Reihenfolge (custom.series_order).
     Der Block "ohne Serie" traegt keine Position und bleibt vorne (-1).
     Serien ohne festgelegten Platz kommen aus dem Liquid mit
     pv_unordered_series_position (9999) - der Wert muss hoeher sein als jede
     echte Position. */
  function positionOf(group) {
    var value = parseInt(group.getAttribute(POSITION_ATTR), 10);
    return isNaN(value) ? -1 : value;
  }

  /* Neuen Serien-Block vor den ersten Block mit hoeherer Position setzen.
     Gleiche Position (z. B. Serien ohne festgelegte Reihenfolge) -> dahinter,
     damit die Fundreihenfolge erhalten bleibt. */
  function insertGroupInOrder(grid, group) {
    var position = positionOf(group);
    var groups = toArray(grid.querySelectorAll(GROUP_SELECTOR));
    for (var i = 0; i < groups.length; i++) {
      if (positionOf(groups[i]) > position) {
        groups[i].parentNode.insertBefore(group, groups[i]);
        return;
      }
    }
    grid.appendChild(group);
  }

  /* Serien-Bloecke der nachgeladenen Seite in das bestehende Grid mergen. */
  function mergeGroups(targetGrid, sourceGrid) {
    toArray(sourceGrid.querySelectorAll(GROUP_SELECTOR)).forEach(function (group) {
      stripDuplicateAssets(group);

      var ref = group.getAttribute(REF_ATTR);
      var existing = ref ? findGroup(targetGrid, ref) : null;
      var targetList = existing ? existing.querySelector('ul.product-grid') : null;

      if (targetList) {
        // Serie existiert bereits -> nur die Karten anhaengen, keine zweite Ueberschrift
        toArray(group.querySelectorAll('li.grid__item')).forEach(function (item) {
          targetList.appendChild(item);
        });
      } else {
        // Serie kommt erstmals vor -> an ihrer Position einsortieren
        insertGroupInOrder(targetGrid, group);
      }
    });
  }

  function revealAnimations(grid) {
    if (typeof window.initializeScrollAnimationTrigger === 'function') {
      try {
        window.initializeScrollAnimationTrigger(grid);
      } catch (e) {
        /* noop */
      }
    }
  }

  function removeLoader(loader) {
    if (loader && loader.parentNode) loader.parentNode.removeChild(loader);
  }

  /* Fallback: klassische Paginierung aus dem <noscript> sichtbar machen. */
  function showPaginationFallback(loader) {
    var noscript = loader.nextElementSibling;
    if (noscript && noscript.tagName === 'NOSCRIPT') {
      var wrapper = document.createElement('div');
      wrapper.innerHTML = noscript.textContent || '';
      noscript.parentNode.insertBefore(wrapper, noscript);
    }
    removeLoader(loader);
  }

  function buildUrl(sectionId, page) {
    var url = new URL(window.location.href);
    url.searchParams.set('page', String(page));
    url.searchParams.set('section_id', sectionId);
    return url.toString();
  }

  function loadPage(loader, grid, page, totalPages) {
    fetch(buildUrl(loader.getAttribute('data-section-id'), page), {
      headers: { 'X-Requested-With': 'XMLHttpRequest' },
    })
      .then(function (response) {
        if (!response.ok) throw new Error('HTTP ' + response.status);
        return response.text();
      })
      .then(function (html) {
        var doc = new DOMParser().parseFromString(html, 'text/html');
        var newGrid = doc.getElementById('product-grid');
        if (newGrid) mergeGroups(grid, newGrid);

        if (page < totalPages) {
          loadPage(loader, grid, page + 1, totalPages);
        } else {
          removeLoader(loader);
          revealAnimations(grid);
          document.dispatchEvent(
            new CustomEvent('pv:series-grid:complete', { detail: { pages: totalPages } })
          );
        }
      })
      .catch(function (error) {
        console.warn('[pv-series-grid] Nachladen fehlgeschlagen:', error);
        revealAnimations(grid);
        showPaginationFallback(loader);
      });
  }

  function init(loader) {
    if (!loader || loader.dataset.pvInitialized === 'true') return;
    loader.dataset.pvInitialized = 'true';

    var container = loader.closest('.product-grid-container') || document;
    var grid = container.querySelector('#product-grid') || document.getElementById('product-grid');
    var totalPages = parseInt(loader.getAttribute('data-total-pages'), 10) || 1;
    var currentPage = parseInt(loader.getAttribute('data-current-page'), 10) || 1;

    if (!grid || totalPages <= 1) {
      removeLoader(loader);
      return;
    }

    // Wird eine Unterseite direkt aufgerufen (?page=2), zurueck auf Seite 1 -
    // dort wird ohnehin alles geladen.
    if (currentPage > 1) {
      var url = new URL(window.location.href);
      url.searchParams.delete('page');
      window.location.replace(url.toString());
      return;
    }

    loadPage(loader, grid, 2, totalPages);
  }

  function initAll(root) {
    toArray((root || document).querySelectorAll('[data-pv-series-loader]')).forEach(init);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      initAll(document);
    });
  } else {
    initAll(document);
  }

  // Nach Filter-/Sortier-Updates (facets.js tauscht das Grid aus) neu initialisieren
  if (window.MutationObserver) {
    new MutationObserver(function (mutations) {
      for (var i = 0; i < mutations.length; i++) {
        var added = mutations[i].addedNodes;
        for (var j = 0; j < added.length; j++) {
          var node = added[j];
          if (node.nodeType !== 1) continue;
          if (node.hasAttribute && node.hasAttribute('data-pv-series-loader')) {
            init(node);
          } else if (node.querySelector) {
            initAll(node);
          }
        }
      }
    }).observe(document.body, { childList: true, subtree: true });
  }
})();
